import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  Card,
  CardContent,
  useTheme,
  alpha,
  Container,
  IconButton,
  Fade,
  Zoom,
  CircularProgress,
} from "@mui/material";
import {
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Google as GoogleIcon,
  Person as PersonIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import LoadingState from "./LoadingState";

const SocialRegistrationChoice = ({
  onSocialChoice,
  onManualChoice,
  eventName,
  eventId,
  availableProviders = [],
  showManualOption = true, // Add prop to control manual option visibility
  loading = false,
  error = null,
}) => {
  const theme = useTheme();
  const [hoveredProvider, setHoveredProvider] = useState(null);
  const [processingProvider, setProcessingProvider] = useState(null);

  const socialProviders = {
    facebook: {
      name: "Facebook",
      icon: FacebookIcon,
      color: "#1877F2",
      description: "Quick registration with your Facebook account",
    },
    linkedin: {
      name: "LinkedIn",
      icon: LinkedInIcon,
      color: "#0A66C2",
      description: "Professional registration with LinkedIn",
    },
    google: {
      name: "Google",
      icon: GoogleIcon,
      color: "#EA4335",
      description: "Fast and secure with your Google account",
    },
  };

  const handleSocialChoice = async (provider) => {
    setProcessingProvider(provider);
    try {
      await onSocialChoice(provider);
    } catch (error) {
      console.error("Social choice error:", error);
    } finally {
      setProcessingProvider(null);
    }
  };

  const handleManualChoice = async () => {
    setProcessingProvider("manual");
    try {
      await onManualChoice();
    } catch (error) {
      console.error("Manual choice error:", error);
    } finally {
      setProcessingProvider(null);
    }
  };

  if (loading) {
    return (
      <LoadingState
        type="registration"
        eventId={eventId}
        message="Loading registration options..."
      />
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: "center", mt: 8 }}>
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            background: "linear-gradient(135deg, #ffebee 0%, #f3e5f5 100%)",
            border: "1px solid #d32f2f",
          }}
        >
          <Typography variant="h5" gutterBottom color="error">
            Error Loading Registration Options
          </Typography>
          <Typography variant="body1" paragraph>
            {error}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please try refreshing the page or contact support if the problem
            persists.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Choose Your Registration Method
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Register for <strong>{eventName}</strong> using your social media
            account for a faster experience, or fill out the form manually.
          </Typography>
        </Box>

        {availableProviders.length > 0 && (
          <Box mb={4}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <SpeedIcon color="primary" />
              Quick Registration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Register in seconds using your existing social media account
            </Typography>

            <Stack spacing={2}>
              {availableProviders.map((provider) => {
                const config = socialProviders[provider];
                if (!config) return null;

                const IconComponent = config.icon;
                const isProcessing = processingProvider === provider;

                return (
                  <Card
                    key={provider}
                    sx={{
                      cursor: isProcessing ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      transform:
                        hoveredProvider === provider && !isProcessing
                          ? "translateY(-2px)"
                          : "translateY(0)",
                      boxShadow:
                        hoveredProvider === provider && !isProcessing
                          ? `0 8px 25px ${alpha(config.color, 0.3)}`
                          : `0 2px 10px ${alpha(config.color, 0.1)}`,
                      border: `2px solid ${
                        hoveredProvider === provider && !isProcessing
                          ? config.color
                          : alpha(config.color, 0.2)
                      }`,
                      opacity: isProcessing ? 0.7 : 1,
                      "&:hover": {
                        transform: isProcessing
                          ? "translateY(0)"
                          : "translateY(-2px)",
                        boxShadow: isProcessing
                          ? `0 2px 10px ${alpha(config.color, 0.1)}`
                          : `0 8px 25px ${alpha(config.color, 0.3)}`,
                      },
                    }}
                    onMouseEnter={() =>
                      !isProcessing && setHoveredProvider(provider)
                    }
                    onMouseLeave={() =>
                      !isProcessing && setHoveredProvider(null)
                    }
                    onClick={() =>
                      !isProcessing && handleSocialChoice(provider)
                    }
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              backgroundColor: alpha(config.color, 0.1),
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.3s ease",
                              ...(hoveredProvider === provider &&
                                !isProcessing && {
                                  backgroundColor: config.color,
                                  transform: "scale(1.1)",
                                }),
                            }}
                          >
                            {isProcessing ? (
                              <CircularProgress
                                size={24}
                                sx={{ color: config.color }}
                              />
                            ) : (
                              <IconComponent
                                sx={{
                                  fontSize: 28,
                                  color:
                                    hoveredProvider === provider
                                      ? "white"
                                      : config.color,
                                  transition: "color 0.3s ease",
                                }}
                              />
                            )}
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color:
                                  hoveredProvider === provider && !isProcessing
                                    ? config.color
                                    : "text.primary",
                              }}
                            >
                              {isProcessing
                                ? "Connecting..."
                                : `Continue with ${config.name}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {config.description}
                            </Typography>
                          </Box>
                        </Box>
                        <ArrowForwardIcon
                          sx={{
                            color:
                              hoveredProvider === provider && !isProcessing
                                ? config.color
                                : "text.disabled",
                            transition: "all 0.3s ease",
                            transform:
                              hoveredProvider === provider && !isProcessing
                                ? "translateX(4px)"
                                : "translateX(0)",
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        )}

        {availableProviders.length > 0 && showManualOption && (
          <Box sx={{ my: 4, position: "relative" }}>
            <Divider />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "background.paper",
                px: 2,
                color: "text.secondary",
                fontSize: "0.875rem",
              }}
            >
              OR
            </Box>
          </Box>
        )}

        {showManualOption && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <PersonIcon color="primary" />
              Manual Registration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Fill out the registration form manually with your details
            </Typography>

            <Card
              sx={{
                cursor:
                  processingProvider === "manual" ? "not-allowed" : "pointer",
                transition: "all 0.3s ease",
                border: `2px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                opacity: processingProvider === "manual" ? 0.7 : 1,
                "&:hover": {
                  transform:
                    processingProvider === "manual"
                      ? "translateY(0)"
                      : "translateY(-2px)",
                  boxShadow:
                    processingProvider === "manual"
                      ? "none"
                      : `0 8px 25px ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderColor:
                    processingProvider === "manual"
                      ? alpha(theme.palette.grey[500], 0.2)
                      : theme.palette.primary.main,
                },
              }}
              onClick={() =>
                processingProvider !== "manual" && handleManualChoice()
              }
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {processingProvider === "manual" ? (
                        <CircularProgress
                          size={24}
                          sx={{ color: theme.palette.primary.main }}
                        />
                      ) : (
                        <PersonIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600 }}
                        color="text.primary"
                      >
                        {processingProvider === "manual"
                          ? "Loading..."
                          : "Fill Registration Form"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Complete registration using the standard form
                      </Typography>
                    </Box>
                  </Box>
                  <ArrowForwardIcon color="primary" />
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        <Box
          sx={{
            mt: 4,
            p: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <SecurityIcon
              sx={{ fontSize: 20, color: theme.palette.primary.main }}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: theme.palette.primary.main }}
            >
              Your data is secure
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            We only access basic information (name, email) from your social
            media account. Your privacy is protected and we never post on your
            behalf.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default SocialRegistrationChoice;
